import { GetWSMgr } from "@/Utils/StateLib";
import { DoFileOperationAsync } from "@/Utils/Promises";
import { FileDropZone } from "@/appComponents/Blocks/Composites/FileDropZone/FileDropZone";
import { getFileIconInfo } from "@/appComponents/Blocks/Composites/FileDirectory/fileIconUtils";
import { appstr } from "@coreapp/utils/appstr";
import { Button, Flex, Icon, Text, ToolIcon } from "@theorgchartdev/storybook";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";

type DragAndDropFileProps = {
  files?: string[];
  onDelete?: (fileName: string) => void;
  targetDirectory?: string;
  allowedExtensions?: string[];
  maxFiles?: number;
  maxFileSizeMB?: number;
};

const DragAndDropFile = ({
  files = [],
  onDelete,
  targetDirectory = "System",
  allowedExtensions,
  maxFiles = 1,
  maxFileSizeMB = 10
}: DragAndDropFileProps) => {
  const wsMgr = GetWSMgr();
  const uploadDisabled = files.length >= maxFiles;
  const { addToast }: any = useToast();

  const handleUpload = () => {
    wsMgr?.UploadFile?.({
      directory: targetDirectory,
      fileTypes: allowedExtensions,
      skipFM: true
    });
  };

  const handleDelete = async (fileName: string) => {
    const accountName = wsMgr?.GetAccountName?.();
    const username = wsMgr?.GetUserName?.();

    if (!accountName || !username) return;

    try {
      await DoFileOperationAsync("delete", {
        names: fileName,
        entitytype: "file",
        thePath: targetDirectory,
        accountName,
        username
      });

      onDelete?.(fileName);
    } catch (error) {
      addToast({
        message: appstr("Failed to delete file. Please try again."),
        status: TOAST_STATUS.ERROR
      });
    }
  };

  return (
    <>
      <FileDropZone
        targetDirectory={targetDirectory}
        allowMultiUpload={maxFiles > 1}
        extensionRestrictions={allowedExtensions}
        className="oc-drag-and-drop-file-zone"
      >
        <Icon name="cloud-arrow-up" size="xxlarge" />
        <Text size="small" modifier="medium">
          {appstr("Drag and drop your file here, or click to browse")}
        </Text>
        <Text size="small" colorModifier="grey">
          {appstr("file formats", {
            formats: allowedExtensions ? allowedExtensions.join(", ") : "all",
            size: maxFileSizeMB
          })}
        </Text>
        <Button
          modifier="secondary"
          disabled={uploadDisabled}
          onClick={handleUpload}
          dataTestId="ddf-upload-btn"
        >
          {appstr("Upload file")}
        </Button>
      </FileDropZone>
      {files.map((fileName, idx) => {
        const ext = fileName.split(".").pop() ?? "";
        const { iconName, color, allowColorChange } = getFileIconInfo(ext);
        const colorClass = allowColorChange ? `ocn-file-directory-icon--${color}` : "";
        return (
          <Flex key={fileName} alignItems="center" justifyContent="space-between">
            <Flex gap="8px" alignItems="center">
              <div className={`ocn-file-directory-icon ${colorClass}`}>
                <Icon name={iconName} size="small" />
              </div>
              <Text colorModifier="grey" dataTestId={`ddf-file-name-${idx}`}>
                {targetDirectory} / {fileName}
              </Text>
            </Flex>
            <ToolIcon
              name="trash-can"
              iconColor="error"
              size="medium-small"
              onClick={() => handleDelete(fileName)}
              dataTestId={`ddf-file-delete-${idx}`}
            />
          </Flex>
        );
      })}
    </>
  );
};

export default DragAndDropFile;
